/**
 * Centered modal — backdrop, close-on-Esc, close-on-overlay-click.
 */
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { classes } from '@/lib/utils';

export default function Modal({
  open, onClose, title, subtitle, children, footer, size = 'md', testID,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  const widthCls = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' }[size];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testID}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-[fadeIn_120ms_ease-out]" onClick={onClose} />
      <div className={classes(
        'relative w-full bg-[#0E0E13] border border-white/10 rounded-2xl shadow-2xl flex flex-col max-h-[100vh] [max-height:100dvh] animate-[popIn_160ms_ease-out]',
        widthCls,
      )}>
        <header className="flex-shrink-0 px-6 py-5 border-b border-white/8 flex items-start justify-between gap-4">
          <div className="min-w-0">
            {title ? <h2 className="font-display text-lg font-semibold">{title}</h2> : null}
            {subtitle ? <p className="text-xs text-white/50 mt-1">{subtitle}</p> : null}
          </div>
          <button type="button" onClick={onClose} data-testid="modal-close"
            className="w-9 h-9 grid place-items-center rounded-lg text-white/60 hover:text-white hover:bg-white/5">
            <X size={18} />
          </button>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-4">{children}</div>
        {footer ? (
          <footer className="sticky bottom-0 z-20 flex-shrink-0 px-6 py-4 border-t border-white/8 bg-[#0E0E13] flex items-center justify-end gap-2">
            {footer}
          </footer>
        ) : null}
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes popIn { from { transform: scale(0.96); opacity: 0 } to { transform: scale(1); opacity: 1 } }
      `}</style>
    </div>
  );
}
